import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Briefcase, MapPin, User, GripVertical, Undo2, Flame } from "lucide-react";

const STAGES = [
  { key: "cold", label: "Cold", dot: "bg-sky-400" },
  { key: "warm", label: "Warm", dot: "bg-amber-400" },
  { key: "hot", label: "Hot", dot: "bg-red-500" },
  { key: "quoted", label: "Quoted", dot: "bg-violet-400" },
  { key: "approved", label: "Approved", dot: "bg-lime-500" },
  { key: "scheduled", label: "Scheduled", dot: "bg-blue-500" },
  { key: "completed", label: "Completed", dot: "bg-emerald-500" },
  { key: "invoiced", label: "Invoiced", dot: "bg-teal-500" },
  { key: "archived", label: "Archived", dot: "bg-slate-400" },
  { key: "deleted", label: "Cancelled", dot: "bg-rose-500" },
];
const ROWS = [["cold", "warm", "hot", "quoted"], ["approved", "scheduled", "completed"], ["invoiced", "archived", "deleted"]];
const byKey = Object.fromEntries(STAGES.map((s) => [s.key, s]));
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("en-AU", { day: "2-digit", month: "short" }); } catch { return ""; } };

export default function JobsPipelinePage() {
  const [jobs, setJobs] = useState([]);
  const [emps, setEmps] = useState({});
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [j, e] = await Promise.all([api.get("/jobs"), api.get("/employees")]);
    setJobs(j.data);
    setEmps(Object.fromEntries(e.data.map((x) => [x.id, x])));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const stageOf = (job) => job.pipeline_stage || "scheduled";

  const applyMove = async (jobId, stage) => {
    setJobs((js) => js.map((x) => x.id === jobId ? { ...x, pipeline_stage: stage } : x));
    try { await api.patch(`/jobs/${jobId}/stage`, { stage }); }
    catch { toast.error("Move failed"); load(); return false; }
    return true;
  };

  const move = async (jobId, stage) => {
    const job = jobs.find((x) => x.id === jobId);
    if (!job || stageOf(job) === stage) return;
    const from = stageOf(job);
    const ok = await applyMove(jobId, stage);
    if (ok) { setLastMove({ jobId, from, to: stage }); toast.success(`Moved to ${byKey[stage].label}`); }
  };

  const restoreLastMove = async () => {
    if (!lastMove) return;
    const { jobId, from, to } = lastMove;
    const ok = await applyMove(jobId, from);
    if (ok) toast.success(`Restored to ${byKey[from].label}`);
    setLastMove(null);
  };

  const onDrop = (stage) => { if (dragId) move(dragId, stage); setDragId(null); setOverStage(null); };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;

  const Column = ({ stageKey }) => {
    const s = byKey[stageKey];
    const items = jobs.filter((j) => stageOf(j) === stageKey);
    const active = overStage === stageKey;
    return (
      <div
        data-testid={`pipeline-col-${stageKey}`}
        onDragOver={(e) => { e.preventDefault(); setOverStage(stageKey); }}
        onDragLeave={() => setOverStage((v) => v === stageKey ? null : v)}
        onDrop={() => onDrop(stageKey)}
        className={`rounded-xl border p-2.5 flex flex-col h-[360px] transition-colors ${active ? "border-lime-400 bg-lime-50" : "border-slate-200 bg-slate-50/60"}`}
      >
        <div className="flex items-center gap-2 px-1 pb-2 shrink-0">
          <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
          <span className="text-sm font-heading font-semibold text-slate-800">{s.label}</span>
          <span className="ml-auto text-xs font-bold text-slate-400" data-testid={`pipeline-count-${stageKey}`}>{items.length}</span>
        </div>
        <div className="space-y-2 flex-1 overflow-y-auto pr-0.5" data-testid={`pipeline-scroll-${stageKey}`}>
          {items.map((job) => (
            <div
              key={job.id}
              draggable
              onDragStart={() => setDragId(job.id)}
              onDragEnd={() => { setDragId(null); setOverStage(null); }}
              onClick={() => navigate(`/jobs/${job.id}`)}
              data-testid={`pipeline-job-${job.id}`}
              className={`group bg-white border border-slate-200 rounded-lg p-2.5 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all ${dragId === job.id ? "opacity-50" : ""}`}
            >
              <div className="flex items-start gap-1.5">
                <GripVertical className="h-4 w-4 text-slate-300 shrink-0 mt-0.5 cursor-grab active:cursor-grabbing" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">{job.title}</p>
                  <div className="mt-1 flex flex-wrap gap-x-2.5 gap-y-0.5 text-[11px] text-slate-500">
                    {job.start && <span>{fmtDate(job.start)}</span>}
                    {job.location && <span className="inline-flex items-center gap-0.5 truncate max-w-[120px]"><MapPin className="h-3 w-3" />{job.location}</span>}
                    {emps[job.employee_id] && <span className="inline-flex items-center gap-0.5"><User className="h-3 w-3" />{emps[job.employee_id].name.split(" ")[0]}</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-[11px] text-slate-300 text-center py-3">Drop jobs here</p>}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 fade-up" data-testid="jobs-pipeline-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Sales Pipeline</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2"><Briefcase className="h-8 w-8 text-lime-500" /> Jobs</h1>
          <p className="text-slate-500 mt-1">All {jobs.length} jobs across the pipeline — drag a card to change its stage.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href="/map/heatmap/fullscreen" target="_blank" rel="noopener noreferrer" data-testid="jobs-heatmap-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium bg-orange-500 hover:bg-orange-400 text-white transition-all">
            <Flame className="h-4 w-4" /> Heat Map
          </a>
          <button
            onClick={restoreLastMove}
            disabled={!lastMove}
            data-testid="restore-last-move-btn"
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${lastMove ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
          >
            <Undo2 className="h-4 w-4" /> Restore last move
            {lastMove && <span className="text-xs opacity-70">({byKey[lastMove.to].label} → {byKey[lastMove.from].label})</span>}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pipeline-row-1">{ROWS[0].map((k) => <Column key={k} stageKey={k} />)}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pipeline-row-2">{ROWS[1].map((k) => <Column key={k} stageKey={k} />)}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pipeline-row-3">{ROWS[2].map((k) => <Column key={k} stageKey={k} />)}</div>
      </div>
    </div>
  );
}
